import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { firmInfo } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      toast({
        title: "Message Sent Successfully!",
        description: response.data.message || "We'll get back to you within 24 hours.",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error Sending Message",
        description: error.response?.data?.detail || "Please try again or call us at (513) 759-0377.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Schedule your consultation today. We're here to answer your questions and help you start your immigration journey.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-navy-900 mb-6">
                  Contact Information
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  Reach out to us through any of these channels. We're ready to help you with your immigration needs.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                  <div className="bg-gold-100 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-gold-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-navy-900 mb-1">Phone</p>
                    <a href={`tel:${firmInfo.phone}`} className="text-gray-600 hover:text-gold-600 transition-colors duration-200">
                      {firmInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                  <div className="bg-gold-100 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-gold-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-navy-900 mb-1">Email</p>
                    <a href={`mailto:${firmInfo.email}`} className="text-gray-600 hover:text-gold-600 transition-colors duration-200 break-all">
                      {firmInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                  <div className="bg-gold-100 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-gold-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-navy-900 mb-1">Address</p>
                    <p className="text-gray-600">{firmInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                  <div className="bg-gold-100 p-3 rounded-lg">
                    <Clock className="w-6 h-6 text-gold-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-navy-900 mb-1">Office Hours</p>
                    <p className="text-gray-600">{firmInfo.hours}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white p-6 rounded-2xl">
                <h3 className="text-xl font-bold mb-3">Schedule a Consultation</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Contact us to schedule a consultation. We'll discuss your case, answer your questions, and provide guidance on the best path forward.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl">
                <h2 className="text-3xl font-bold text-navy-900 mb-6">
                  Send Us a Message
                </h2>
                <p className="text-gray-600 mb-8">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-navy-900 font-semibold mb-2 block">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="border-gray-300 focus:ring-gold-500 focus:border-gold-500"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-navy-900 font-semibold mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="border-gray-300 focus:ring-gold-500 focus:border-gold-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-navy-900 font-semibold mb-2 block">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="border-gray-300 focus:ring-gold-500 focus:border-gold-500"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-navy-900 font-semibold mb-2 block">
                        Subject *
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Immigration Consultation"
                        className="border-gray-300 focus:ring-gold-500 focus:border-gold-500"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-navy-900 font-semibold mb-2 block">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      placeholder="Please describe your immigration situation and how we can help..."
                      className="border-gray-300 focus:ring-gold-500 focus:border-gold-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-navy-700 to-navy-900 hover:from-navy-800 hover:to-navy-950 text-white py-6 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Send Message
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map/Additional Info Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white p-12 rounded-2xl text-center">
            <h2 className="text-4xl font-bold mb-4">
              Ready to Start Your Immigration Journey?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Don't wait to secure your future. Contact us today and take the first step toward achieving your American dream.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${firmInfo.phone}`}
                className="inline-block bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-navy-900 px-8 py-4 rounded-xl font-bold shadow-xl hover:shadow-gold-500/50 transition-all duration-300 hover:scale-105"
              >
                Call Now: {firmInfo.phone}
              </a>
              <a
                href={`mailto:${firmInfo.email}`}
                className="inline-block border-2 border-white text-white hover:bg-white hover:text-navy-900 px-8 py-4 rounded-xl font-bold transition-all duration-300 hover:scale-105"
              >
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
